import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACK_END_HOST || process.env.REACT_APP_API_URL || 'https://giftedict-backend.onrender.com';

/**
 * PersonDetailSheet - Compact, visual gift suggestion viewer
 * - Horizontal carousel to reduce scrolling
 * - Quick approve/reject actions
 * - Optional comments
 */

// Category icons
const CATEGORY_ICONS = {
  experience: '🎭',
  tech: '📱',
  fashion: '👗',
  home: '🏠',
  food: '🍫',
  wellness: '🧘',
  hobby: '🎨',
  personalized: '💝',
  default: '🎁'
};

// Occasion icons
const getOccasionIcon = (occasion) => {
  const icons = {
    birthday: '🎂',
    anniversary: '💝',
    wedding: '💍',
    graduation: '🎓',
    christmas: '🎄',
    thanksgiving: '🦃',
    default: '🎉'
  };
  return icons[occasion?.toLowerCase()] || icons.default;
};

// Compact Suggestion Card
function CompactSuggestionCard({ suggestion, onApprove, onReject, isProcessing, isActive }) {
  const [showDetails, setShowDetails] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const icon = CATEGORY_ICONS[suggestion.category] || CATEGORY_ICONS.default;

  const handleReject = () => {
    if (showComment && comment) {
      onReject(suggestion.id, comment);
      setShowComment(false);
      setComment('');
    } else {
      onReject(suggestion.id, '');
    }
  };

  return (
    <div className={`
      flex-shrink-0 w-80 rounded-2xl overflow-hidden transition-all duration-300
      ${suggestion.isFeatured
        ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300'
        : 'bg-white border-2 border-slate-200'
      }
      ${isActive ? 'scale-105 shadow-2xl' : 'scale-95 opacity-80'}
    `}>
      {/* Featured Badge */}
      {suggestion.isFeatured && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold py-1.5 px-3 text-center">
          ⭐ TOP PICK
        </div>
      )}

      <div className="p-4">
        {/* Header - Icon, Name, Price */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-2xl flex-shrink-0">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base text-slate-900 leading-tight mb-1 line-clamp-2">
              {suggestion.name}
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-indigo-600">{suggestion.price}</span>
              {suggestion.store && (
                <span className="text-xs text-slate-500">• {suggestion.store}</span>
              )}
            </div>
          </div>
        </div>

        {/* Why it fits - Compact */}
        {suggestion.whyItFits && !showDetails && (
          <p className="text-sm text-slate-600 mb-3 line-clamp-2">
            {suggestion.whyItFits}
          </p>
        )}

        {/* Expanded Details */}
        {showDetails && (
          <div className="mb-3 space-y-2">
            {suggestion.description && (
              <p className="text-sm text-slate-600">{suggestion.description}</p>
            )}
            {suggestion.whyItFits && (
              <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-100">
                <p className="text-xs font-medium text-emerald-800 mb-1">💡 Why it's perfect:</p>
                <p className="text-sm text-emerald-700">{suggestion.whyItFits}</p>
              </div>
            )}
            {suggestion.link && (
              <a
                href={suggestion.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Product
              </a>
            )}
          </div>
        )}

        {/* Toggle Details Button */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-xs text-indigo-600 hover:text-indigo-700 font-medium mb-3 flex items-center gap-1"
        >
          {showDetails ? '▲ Show Less' : '▼ Show More'}
        </button>

        {/* Comment Section */}
        {showComment && (
          <div className="mb-3">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note (optional)"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              rows={2}
            />
          </div>
        )}

        {/* Actions - Compact */}
        <div className="space-y-2">
          <button
            onClick={() => onApprove(suggestion.id)}
            disabled={isProcessing}
            className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          >
            {isProcessing ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                I Love This!
              </>
            )}
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={isProcessing}
              className="flex-1 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg font-medium transition-all text-sm disabled:opacity-50"
            >
              Pass
            </button>
            <button
              onClick={() => setShowComment(!showComment)}
              className="flex-1 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg font-medium transition-all text-sm flex items-center justify-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main PersonDetailSheet Component
export function PersonDetailSheet({ person, onClose }) {
  const [suggestions, setSuggestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const token = localStorage.getItem('authtoken');

  // Calculate days until occasion
  const getDaysUntil = () => {
    if (!person.date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const occasionDate = new Date(person.date);
    if (occasionDate < today) {
      occasionDate.setFullYear(today.getFullYear() + 1);
    }
    const diffTime = occasionDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysUntil = getDaysUntil();
  const occasionIcon = getOccasionIcon(person.occasion);

  // Fetch suggestions
  useEffect(() => {
    if (!person?.id) return;

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/gifts/${person.id}/suggestions`, {
          headers: { 'auth-token': token }
        });
        const data = await response.json();
        if (data.success) {
          const pending = (data.data || []).filter(s => s.status === 'pending');
          setSuggestions(pending);
        }
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setError('Failed to load suggestions');
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [person?.id, token]);

  const handleApprove = async (suggestionId) => {
    setProcessingId(suggestionId);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/gifts/suggestions/${suggestionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({ status: 'approved' })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage("Perfect! We'll handle everything 🎉");
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        setTimeout(() => setSuccessMessage(null), 3000);

        // Move to next suggestion if available
        if (currentIndex >= suggestions.length - 1) {
          setCurrentIndex(Math.max(0, currentIndex - 1));
        }
      } else {
        setError(data.error || 'Failed to approve suggestion');
      }
    } catch (err) {
      console.error('Error approving suggestion:', err);
      setError('Failed to approve suggestion');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (suggestionId, feedback) => {
    setProcessingId(suggestionId);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/gifts/suggestions/${suggestionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({
          status: 'rejected',
          userNotes: feedback || null
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        if (feedback) {
          setSuccessMessage("Thanks for the feedback!");
          setTimeout(() => setSuccessMessage(null), 3000);
        }

        // Move to next suggestion if available
        if (currentIndex >= suggestions.length - 1) {
          setCurrentIndex(Math.max(0, currentIndex - 1));
        }
      } else {
        setError(data.error || 'Failed to reject suggestion');
      }
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
      setError('Failed to update suggestion');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-slate-50 to-white">
      {/* Ultra-Compact Header - Single Line */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2 text-sm">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-400 to-coral-600 flex items-center justify-center text-white font-bold text-base shadow-md flex-shrink-0">
            {person.recipientName?.charAt(0)?.toUpperCase() || '?'}
          </div>

          {/* All Info in One Line */}
          <div className="flex items-center gap-1.5 flex-wrap text-slate-700">
            <span className="font-bold">{person.recipientName}</span>
            <span>•</span>
            <span>{occasionIcon}</span>
            <span className="capitalize text-xs">{person.occasion}</span>
            {daysUntil !== null && (
              <>
                <span>•</span>
                <span className={`text-xs font-medium ${daysUntil <= 7 ? 'text-red-600' : ''}`}>
                  {daysUntil === 0 ? 'Today' : `${daysUntil}d`}
                </span>
              </>
            )}
            {person.interests && (
              <>
                <span>•</span>
                <span className="text-xs">💡 {person.interests}</span>
              </>
            )}
            {person.budget && (
              <>
                <span>•</span>
                <span className="text-xs">💰 ${person.budget}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mx-4 mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
          <span className="text-emerald-600 text-xl">✓</span>
          <p className="text-sm font-medium text-emerald-800">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Suggestions Carousel */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-sm text-slate-500">Loading...</p>
            </div>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="flex items-center justify-center h-full px-4">
            <div className="text-center max-w-sm">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🎁</span>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">No Suggestions Yet</h3>
              <p className="text-sm text-slate-500">
                Our experts are finding the perfect gifts. Check back soon!
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col justify-center py-6">
            {/* Carousel Counter */}
            <div className="px-4 mb-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  🎯 {currentIndex + 1} of {suggestions.length} suggestions
                </p>
                <div className="flex gap-1">
                  {suggestions.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentIndex ? 'bg-indigo-500 w-6' : 'bg-slate-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Carousel */}
            <div className="px-4 overflow-x-auto scrollbar-hide">
              <div className="flex gap-4 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
                {suggestions.map((suggestion, idx) => (
                  <CompactSuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    isProcessing={processingId === suggestion.id}
                    isActive={idx === currentIndex}
                  />
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="px-4 mt-4 flex gap-3">
              <button
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
                className="flex-1 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium disabled:opacity-30 hover:bg-slate-200 transition-all"
              >
                ← Previous
              </button>
              <button
                onClick={() => setCurrentIndex(Math.min(suggestions.length - 1, currentIndex + 1))}
                disabled={currentIndex === suggestions.length - 1}
                className="flex-1 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium disabled:opacity-30 hover:bg-slate-200 transition-all"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PersonDetailSheet;
